/**
 * Lane resolution: default an AI edit to `act` or `propose` by the containment chain
 * (Workspace → Project → Collection → Document). Specificity may only TIGHTEN the lane:
 * the most-restrictive default across ALL defined scope levels wins, so a stricter ancestor
 * can never be loosened by a looser, more-specific level (AC 6.2, OQ-3). Team is an
 * actor-level fallback only.
 *
 * The floor is enforced HERE and again in `transactions/apply.ts` so it can never be
 * bypassed by a caller passing an optimistic `requestedLane`.
 */

import type { Lane, ScopeChain } from '../types';

/** Per-scope default lane configuration, keyed by scope id. */
export interface LanePolicy {
  workspace?: Record<string, Lane>;
  project?: Record<string, Lane>;
  collection?: Record<string, Lane>;
  document?: Record<string, Lane>;
  /** Applied when nothing more specific matches. */
  fallback: Lane;
}

export interface LaneResolver {
  resolve(scope: ScopeChain, requestedLane?: Lane): Lane;
}

/** `propose` is strictly more restrictive than `act`. The floor picks the more restrictive of two. */
export function mostRestrictive(a: Lane, b: Lane): Lane {
  return a === 'propose' || b === 'propose' ? 'propose' : 'act';
}

export class DefaultLaneResolver implements LaneResolver {
  constructor(private readonly policy: LanePolicy) {}

  resolve(scope: ScopeChain, requestedLane?: Lane): Lane {
    // Most-restrictive-wins across EVERY defined scope level (AC 6.2 / TC-6.2). Specificity may
    // only TIGHTEN the lane, never loosen it: a more-restrictive ancestor (e.g. a workspace
    // `propose`) always clamps a looser, more-specific default (e.g. a document `act`). We fold
    // the floor down the whole containment chain rather than taking only the most-specific level.
    const defined = [
      this.policy.document?.[scope.documentId],
      scope.collectionId ? this.policy.collection?.[scope.collectionId] : undefined,
      scope.projectId ? this.policy.project?.[scope.projectId] : undefined,
      scope.workspaceId ? this.policy.workspace?.[scope.workspaceId] : undefined,
    ].filter((lane): lane is Lane => lane !== undefined);

    // Fallback only applies when NO level defines a default; otherwise the defined levels' floor wins.
    const specific = defined.length > 0 ? defined.reduce(mostRestrictive) : this.policy.fallback;

    // Safety floor: a caller can only ever tighten, never loosen, the resolved default.
    return requestedLane ? mostRestrictive(specific, requestedLane) : specific;
  }
}
